import { cn } from '@paguz/ui';
import type { ReactNode } from 'react';
import { diffLines } from '../lib/line-diff.ts';

/**
 * Vista previa de markdown (subconjunto: títulos, listas, tablas, citas, negrita, cursiva y código).
 * Construye elementos React (sin `innerHTML`), suficiente para artículos de la KB y skills.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks: ReactNode[] = [];
  const lines = source.split('\n');
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) {
      i++;
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]?.length ?? 1;
      const cls =
        level === 1
          ? 'font-display text-xl font-semibold text-foreground'
          : level === 2
            ? 'mt-2 font-display text-lg font-semibold text-foreground'
            : 'font-semibold text-foreground';
      blocks.push(
        <p key={key++} role="heading" aria-level={level + 1} className={cls}>
          {inline(heading[2] ?? '')}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('|')) {
        const raw = lines[i] ?? '';
        if (!/^\|[\s:|-]+\|$/.test(raw))
          rows.push(
            raw
              .split('|')
              .slice(1, -1)
              .map((c) => c.trim()),
          );
        i++;
      }
      const [head, ...body] = rows;
      blocks.push(
        <table key={key++} className="w-full border-collapse text-sm">
          {head && (
            <thead>
              <tr>
                {head.map((c, ci) => (
                  <th key={ci} className="border-b border-border px-2 py-1 text-left font-semibold">
                    {inline(c)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, ci) => (
                  <td key={ci} className="border-b border-border px-2 py-1">
                    {inline(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\s*([-*]|\d+\.)\s+/, ''));
        i++;
      }
      const List = ordered ? 'ol' : 'ul';
      blocks.push(
        <List key={key++} className={cn('pl-5', ordered ? 'list-decimal' : 'list-disc')}>
          {items.map((it, ii) => (
            <li key={ii}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }
    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('>')) {
        quote.push((lines[i] ?? '').replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(
        <blockquote
          key={key++}
          className="border-l-4 border-highlight/50 pl-3 text-muted-foreground"
        >
          {inline(quote.join(' '))}
        </blockquote>,
      );
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() &&
      !/^(#|\||>|\s*([-*]|\d+\.)\s)/.test(lines[i] ?? '')
    ) {
      para.push(lines[i] ?? '');
      i++;
    }
    blocks.push(<p key={key++}>{inline(para.join(' '))}</p>);
  }
  return (
    <div className={cn('flex flex-col gap-3 text-sm leading-relaxed', className)}>{blocks}</div>
  );
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|«[^»]+»)/g;
  let last = 0;
  let k = 0;
  for (const match of text.matchAll(re)) {
    const token = match[0];
    const at = match.index;
    if (at > last) out.push(text.slice(last, at));
    if (token.startsWith('**')) out.push(<strong key={k++}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith('`'))
      out.push(
        <code key={k++} className="rounded bg-muted px-1 font-mono text-xs">
          {token.slice(1, -1)}
        </code>,
      );
    else if (token.startsWith('«'))
      out.push(
        <mark key={k++} className="rounded bg-highlight-soft px-0.5 text-highlight-soft-foreground">
          {token.slice(1, -1)}
        </mark>,
      );
    else out.push(<em key={k++}>{token.slice(1, -1)}</em>);
    last = at + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Diferencias por línea entre dos versiones. */
export function DiffView({
  before,
  after,
  labels,
}: {
  before: string;
  after: string;
  labels: [string, string];
}) {
  const lines = diffLines(before, after);
  const added = lines.filter((l) => l.kind === 'added').length;
  const removed = lines.filter((l) => l.kind === 'removed').length;
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <p className="border-b border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
        {labels[0]} → {labels[1]} · <span className="font-semibold text-success">+{added}</span>{' '}
        <span className="font-semibold text-destructive">−{removed}</span>
      </p>
      <pre className="max-h-96 overflow-auto font-mono text-xs leading-5">
        {lines.map((l, idx) => (
          <div
            key={idx}
            className={cn(
              'px-3 whitespace-pre-wrap',
              l.kind === 'added' && 'bg-success-soft text-success-soft-foreground',
              l.kind === 'removed' && 'bg-danger-soft text-danger-soft-foreground line-through',
            )}
          >
            {l.kind === 'added' ? '+ ' : l.kind === 'removed' ? '− ' : '  '}
            {l.text || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
}
